import { useState } from "react"
import styled from "styled-components"

import { Badge, Skeleton, spacing, Text } from "@minuthq/meatball-ui-react"

import { useFetchHome } from "src/data/organizations/queries/homeQueries"
import { TPropertyContext } from "src/data/propertyContext/propertyContextTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { MDialog } from "src/ui/Dialog/MDialog"
import { MCard } from "src/ui/MCard/MCard"
import { MMarkdown } from "src/ui/MMarkdown/MMarkdown"
import { RadioGroup } from "src/ui/RadioGroup/RadioGroup"

import { AiAssistantSuggestionRadioSelect } from "./AiAssistantSuggestionRadioSelect"

export function AiAssistantAddSuggestionDialog({
  context,
  onClose,
  onConfirm,
}: {
  context: TPropertyContext
  onClose: () => void
  onConfirm: ({
    createNewContent,
    existingContext,
  }: {
    createNewContent: boolean
    existingContext?: TPropertyContext
  }) => void
}) {
  const { t, langKeys, tPlaceholder } = useTranslate()

  const [selectedOption, setSelectedOption] = useState<{
    type: "existing" | "new"
    context?: TPropertyContext
  } | null>(null)

  const fetchHome = useFetchHome({
    orgId: context.organization_id,
    homeId: context.home_id ?? "",
    options: {
      enabled: !!context.home_id,
    },
  })

  function renderHome() {
    if (fetchHome.isInitialLoading) {
      return <Skeleton variant="circle" width="10ch" height={18} />
    }

    if (fetchHome.data) {
      return <Badge>{fetchHome.data.name}</Badge>
    }

    return <Badge color="warning">{tPlaceholder("Unknown")}</Badge>
  }

  return (
    <MDialog
      open={true}
      onClose={onClose}
      title={tPlaceholder("Add suggestion")}
      onConfirm={() => {
        if (selectedOption) {
          onConfirm({
            createNewContent: selectedOption.type === "new",
            existingContext: selectedOption.context,
          })
        }
      }}
      confirmLabel={t(langKeys.continue)}
      confirmButtonProps={{
        disabled: !selectedOption,
      }}
    >
      <MCard border boxShadow={false}>
        <Text variant="subtitle">{context.title}</Text>
        <MMarkdown>{context.content}</MMarkdown>

        <PropertyWrapper>
          <Text>{tPlaceholder("Property")}:</Text>

          {renderHome()}
        </PropertyWrapper>
      </MCard>

      <SelectionWrapper>
        <Text marginBottom={spacing.XS}>
          {tPlaceholder(
            "Choose the content you'd like to apply this suggestion to."
          )}
        </Text>

        <AiAssistantSuggestionRadioSelect
          value={selectedOption?.context?.id ?? ""}
          onChange={(context) => {
            setSelectedOption({
              type: "existing",
              context,
            })
          }}
        />

        <div>
          <Text>{tPlaceholder("Or")}</Text>
          <RadioGroup
            value={selectedOption?.type}
            options={[
              {
                value: "new",
                render: () => <Text>{tPlaceholder("Add new content")}</Text>,
              },
            ]}
            onChange={() => {
              setSelectedOption({
                type: "new",
              })
            }}
            hideRadio
          />
        </div>
      </SelectionWrapper>
    </MDialog>
  )
}

const PropertyWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.XS};

  margin-top: ${spacing.M};
`

const SelectionWrapper = styled.div`
  display: grid;
  gap: ${spacing.L};
  margin-top: ${spacing.L};
`
